import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { User, ArrowLeft, LogIn, UserPlus } from "lucide-react";
import { api } from "@/lib/api";
import { useLang } from "@/lib/lang";

export default function CreatorAuth() {
  const navigate = useNavigate();
  const { t } = useLang();
  const [mode, setMode] = useState("login");
  const [form, setForm] = useState({ name: "", email: "", password: "", company_name: "", bio: "", tier: "starter" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      let result;
      if (mode === "login") {
        result = await api.creatorLogin({ email: form.email, password: form.password });
      } else {
        result = await api.creatorRegister(form);
      }
      localStorage.setItem("creator_token", result.token);
      localStorage.setItem("creator_id", result.creator_id);
      localStorage.setItem("creator_email", result.email);
      navigate("/creator/dashboard");
    } catch (err) {
      setError(err.response?.data?.detail || "Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  const inputClass = "w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white placeholder-white/30 focus:outline-none focus:ring-2 focus:ring-[#00F0FF]/50 transition-all text-sm";

  return (
    <div className="min-h-screen bg-[#05050A] flex items-center justify-center px-6" data-testid="creator-auth-page">
      <div className="orb w-[400px] h-[400px] bg-[#FF007F]/10 -top-20 -right-20" />
      <div className="orb w-[300px] h-[300px] bg-[#00F0FF]/10 bottom-20 -left-20" />

      <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-md relative z-10">
        <Link to="/" className="flex items-center gap-2 text-[#A0A0AB] hover:text-[#00F0FF] transition-colors mb-8">
          <ArrowLeft className="w-4 h-4" /> Back to site
        </Link>

        <div className="glass-card rounded-3xl p-8">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-12 h-12 rounded-2xl bg-[#FF007F]/10 flex items-center justify-center">
              <User className="w-6 h-6 text-[#FF007F]" />
            </div>
            <div>
              <h1 className="font-['Unbounded'] text-xl font-bold text-white">{t("creator_portal")}</h1>
              <p className="text-[#A0A0AB] text-xs">{t("creator_sell")}</p>
            </div>
          </div>

          {/* Toggle */}
          <div className="flex gap-2 mb-6 p-1 bg-white/5 rounded-xl">
            <button
              data-testid="creator-login-tab"
              onClick={() => setMode("login")}
              className={`flex-1 flex items-center justify-center gap-2 py-2 rounded-lg text-sm font-medium transition-all ${mode === "login" ? "bg-[#00F0FF]/10 text-[#00F0FF]" : "text-[#A0A0AB]"}`}
            >
              <LogIn className="w-4 h-4" /> {t("creator_signin")}
            </button>
            <button
              data-testid="creator-register-tab"
              onClick={() => setMode("register")}
              className={`flex-1 flex items-center justify-center gap-2 py-2 rounded-lg text-sm font-medium transition-all ${mode === "register" ? "bg-[#00F0FF]/10 text-[#00F0FF]" : "text-[#A0A0AB]"}`}
            >
              <UserPlus className="w-4 h-4" /> {t("creator_register")}
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4" data-testid="creator-auth-form">
            {mode === "register" && (
              <>
                <div>
                  <label className="text-xs text-[#A0A0AB] mb-1 block">Full Name</label>
                  <input data-testid="creator-name" className={inputClass} value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} required placeholder="Your name" />
                </div>
                <div>
                  <label className="text-xs text-[#A0A0AB] mb-1 block">Company / Brand Name</label>
                  <input data-testid="creator-company" className={inputClass} value={form.company_name} onChange={(e) => setForm({...form, company_name: e.target.value})} placeholder="Optional" />
                </div>
              </>
            )}
            <div>
              <label className="text-xs text-[#A0A0AB] mb-1 block">Email</label>
              <input data-testid="creator-email" type="email" className={inputClass} value={form.email} onChange={(e) => setForm({...form, email: e.target.value})} required placeholder="you@email.com" />
            </div>
            <div>
              <label className="text-xs text-[#A0A0AB] mb-1 block">Password</label>
              <input data-testid="creator-password" type="password" className={inputClass} value={form.password} onChange={(e) => setForm({...form, password: e.target.value})} required placeholder="Min 6 characters" />
            </div>

            {error && <p className="text-red-400 text-sm" data-testid="creator-auth-error">{error}</p>}

            <button data-testid="creator-auth-submit" type="submit" disabled={loading} className="neon-btn w-full py-3 text-center text-sm disabled:opacity-50">
              {loading ? t("creator_please_wait") : (mode === "login" ? t("creator_signin") : t("creator_create"))}
            </button>
          </form>

          {mode === "register" && (
            <p className="text-[#A0A0AB] text-xs text-center mt-4">
              By registering, you start on the <span className="text-[#00F0FF]">Starter (Free)</span> plan. <Link to="/pricing" className="text-[#00F0FF] hover:underline">View plans</Link>
            </p>
          )}
        </div>
      </motion.div>
    </div>
  );
}
